// ContactUs.js

import React from 'react';
import styled from 'styled-components';
import { 
  FaMapMarkerAlt, FaEnvelope, FaBuilding, 
  FaClipboardCheck 
} from 'react-icons/fa';

const PageWrapper = styled.div`
  background: radial-gradient(circle at 50% 0%, #151a2e 0%, #0a0d16 100%);
  color: #e2e8f0;
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
  overflow-x: hidden;
  padding-bottom: 80px;
`;

const HeroSection = styled.div`
  padding: 100px 20px 60px;
  text-align: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 600px;
    height: 300px;
    background: radial-gradient(circle, rgba(75, 89, 247, 0.15) 0%, rgba(0,0,0,0) 70%);
    z-index: 0;
    pointer-events: none;
  }
`;

const HeroTitle = styled.h1`
  font-family: 'Outfit', sans-serif;
  font-size: 3.5rem;
  font-weight: 800;
  color: #fff;
  margin-bottom: 15px;
  background: linear-gradient(135deg, #ffffff 30%, #a5b4fc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  position: relative;
  z-index: 1;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled.p`
  color: #94a3b8;
  font-size: 1.1rem;
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`;

const ContactGrid = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 0 40px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  justify-content: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding: 0 20px;
    gap: 20px;
  }
`;

const InfoCard = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 16px;
  padding: 30px;
  backdrop-filter: blur(12px);
  display: flex;
  gap: 20px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(75, 89, 247, 0.3);
    background: rgba(255, 255, 255, 0.04);
    box-shadow: 0 12px 30px rgba(0, 0, 0, 0.25);
  }
`;

const IconWrapper = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  background: rgba(75, 89, 247, 0.1);
  border: 1px solid rgba(75, 89, 247, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #818cf8;
  font-size: 1.35rem;
  flex-shrink: 0;
`;

const InfoContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const InfoTitle = styled.h3`
  font-family: 'Outfit', sans-serif;
  font-size: 1.25rem;
  font-weight: 600;
  color: #fff;
  margin: 0;
`;

const InfoText = styled.p`
  font-size: 0.95rem;
  color: #94a3b8;
  line-height: 1.6;
  margin: 0;
`;

const InfoLink = styled.a`
  color: #818cf8;
  text-decoration: none;
  font-weight: 500;
  transition: color 0.2s ease;
  border-bottom: 1px dashed rgba(129, 140, 248, 0.3);
  padding-bottom: 2px;

  &:hover {
    color: #a5b4fc;
    border-bottom-color: #a5b4fc;
  }
`;

const BadgeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 5px;
`;

const BadgeItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  color: #c7d2fe;
`;

const ContactUs = () => {
  return (
    <PageWrapper>
      <HeroSection>
        <HeroTitle>Contact Us</HeroTitle>
        <HeroSubtitle>
          Need to get in touch? Here is the official business information for Mount Software Service.
        </HeroSubtitle>
      </HeroSection>

      <ContactGrid>
        <InfoCard style={{ gridColumn: '1 / -1' }}>
          <IconWrapper>
            <FaMapMarkerAlt />
          </IconWrapper>
          <InfoContent>
            <InfoTitle>Registered Office Address</InfoTitle>
            <InfoText>
              Mount Software Service<br />
              S-3, 646, BR Apartment, Shalimar Garden Extn 1, Sahibabad,<br />
              Ghaziabad, Uttar Pradesh, 201005
            </InfoText>
          </InfoContent>
        </InfoCard>

        <InfoCard>
          <IconWrapper>
            <FaBuilding />
          </IconWrapper>
          <InfoContent>
            <InfoTitle>Business Registry</InfoTitle>
            <BadgeList>
              <BadgeItem>
                <FaClipboardCheck style={{ color: '#818cf8', fontSize: '0.95rem' }} />
                <span><strong>GSTIN:</strong> 09ASOPC6084A1ZS</span>
              </BadgeItem>
              <BadgeItem>
                <FaClipboardCheck style={{ color: '#818cf8', fontSize: '0.95rem' }} />
                <span><strong>Constitution:</strong> Proprietorship</span>
              </BadgeItem>
            </BadgeList>
          </InfoContent>
        </InfoCard>

        <InfoCard>
          <IconWrapper>
            <FaEnvelope />
          </IconWrapper>
          <InfoContent>
            <InfoTitle>Email Support</InfoTitle>
            <InfoText>
              For general support and inquiries:<br />
              <InfoLink href="mailto:info@mounts.in">info@mounts.in</InfoLink>
            </InfoText>
          </InfoContent>
        </InfoCard>

        {/*<InfoCard style={{ gridColumn: '1 / -1' }}>*/}
        {/*  <IconWrapper>*/}
        {/*    <FaPhone />*/}
        {/*  </IconWrapper>*/}
        {/*  <InfoContent>*/}
        {/*    <InfoTitle>Phone Number</InfoTitle>*/}
        {/*    <InfoText>*/}
        {/*      Reach out to us directly at:<br />*/}
        {/*      <InfoLink href="tel:+919978643408">+91 99786 43408</InfoLink>*/}
        {/*    </InfoText>*/}
        {/*  </InfoContent>*/}
        {/*</InfoCard>*/}
      </ContactGrid>
    </PageWrapper>
  );
};

export default ContactUs;
